/**
 * 개발요청 API — backend /dev-requests/* (v2.244.0 · 2026-10-13 → v2.274.0 전 메뉴 · 전 직원)
 *
 * 어느 화면에서든 오른쪽 위 [📮 개발요청] 단추로 쓰고, 「📮 개발요청」 메뉴(옛 개발 계획 자리 · /dev-plan) 게시판에 쌓인다.
 * 쓰고 보는 사람 = 전 직원(조회 전용 포함) · 상태 바꾸기 = 관리자.
 * 올라온 요청은 부장님 PC 의 루틴(평일 9~19시 2시간마다)이 검토해 **계획을 🤖 답글로** 남기고 상태를 정한다
 * — 루틴은 이 API 가 아니라 `ops/devreq-bot.mjs`(ssh·psql)로 적는다. 개발은 부장님이 모아서 한 번에 시작한다.
 */

import { api } from './api';
import type { RichDoc } from './rich-doc';

/**
 * ⛔서버 `lib/dev-request/store.ts` · DB CHECK(seq_0013) · 봇 스크립트와 **같은 목록**.
 *   어긋나면 화면에서 고른 상태를 서버가 400 으로 되돌린다.
 */
export const DEV_REQUEST_STATUSES = ['요청', '처리중', '보류', '처리완료', '거부'] as const;
export type DevRequestStatus = (typeof DEV_REQUEST_STATUSES)[number];

/** 화면 이름·색 — 이 표 하나가 목록·상세 **양쪽**을 그린다 */
export const STATUS_META: Record<DevRequestStatus, { icon: string; chip: string; hint: string }> = {
  요청: { icon: '📨', chip: 'bg-amber-50 text-amber-700 border-amber-200', hint: '올라온 요청 — 다음 검토(평일 9~19시 2시간마다) 때 🤖 가 계획을 답글로 남깁니다' },
  처리중: { icon: '🛠', chip: 'bg-blue-50 text-blue-700 border-blue-200', hint: '진행할 만한 요청 — 계획을 세워 두었습니다(답글). 요청이 모이면 부장님이 한 번에 개발을 시작합니다' },
  보류: { icon: '⏸', chip: 'bg-violet-50 text-violet-700 border-violet-200', hint: '애매하거나 정할 것이 있어 멈춰 둔 요청 — 답글의 질문에 댓글로 답하거나 글을 고치면 다음 검토 때 다시 봅니다' },
  처리완료: { icon: '✅', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', hint: '운영에 반영됐습니다' },
  거부: { icon: '⛔', chip: 'bg-slate-100 text-slate-500 border-slate-300', hint: '다른 기능과 충돌하거나 위험해서 하지 않기로 했습니다 — 사유는 답글에' },
};

/** 쓴 사람이 고치고 지울 수 있는 상태 — 서버 `AUTHOR_EDITABLE_STATUSES` 와 같은 목록(판정은 서버가 canEdit 로 내려준다) */
export const AUTHOR_EDITABLE_STATUSES: readonly DevRequestStatus[] = ['요청', '보류'];

/** 개발요청에 붙는 첨부 종류 — 서버 `DEVREQ_ATTACH_KIND` 와 같은 글자 */
export const DEVREQ_ATTACH_KIND = '첨부';

export interface DevRequestRow {
  id: number;
  menuKey: string;
  title: string;
  status: DevRequestStatus;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  statusAt: string | null;
  commentCount: number;
  attachmentCount: number;
  lastActivityAt: string;
  lastCommentIsBot: boolean | null;
}

export interface DevRequestComment {
  id: number;
  body: string;
  author: string | null;
  authorName: string | null;
  isBot: boolean;
  statusTo: DevRequestStatus | null;
  createdAt: string;
}

export interface DevRequestDetail extends DevRequestRow {
  doc: RichDoc;
  statusBy: string | null;
  statusByName: string | null;
  updatedBy: string | null;
  updatedByName: string | null;
  comments: DevRequestComment[];
  /** 고치기·지우기 — 관리자 또는 쓴 사람(「요청」·「보류」일 때만) · 서버가 판정해 내려준다 */
  canEdit: boolean;
  /** 상태 바꾸기 — 관리자만 */
  canSetStatus: boolean;
  /** 파일 붙이기 — 쓴 사람·관리자 (지우기는 올린 사람·관리자 — 첨부 통로가 판정) */
  canAttach: boolean;
}

/** 요청 목록 — menuKey 를 주면 그 메뉴만, 없으면 전 메뉴 */
export async function listRequests(menuKey?: string): Promise<DevRequestRow[]> {
  const q = menuKey ? `?menuKey=${encodeURIComponent(menuKey)}` : '';
  const r = await api.get<{ requests: DevRequestRow[] }>(`/dev-requests${q}`);
  return r.requests;
}

export function getRequest(id: number): Promise<DevRequestDetail> {
  return api.get<DevRequestDetail>(`/dev-requests/${id}`);
}

export function createRequest(p: { menuKey: string; title: string; doc: RichDoc }): Promise<{ id: number }> {
  return api.post<{ id: number }>('/dev-requests', p);
}

export function updateRequest(id: number, p: { title: string; doc: RichDoc }): Promise<{ id: number }> {
  return api.put<{ id: number }>(`/dev-requests/${id}`, p);
}

export function deleteRequest(id: number): Promise<{ deleted: boolean; files: number }> {
  return api.delete<{ deleted: boolean; files: number }>(`/dev-requests/${id}`);
}

export function addComment(id: number, body: string): Promise<{ id: number }> {
  return api.post<{ id: number }>(`/dev-requests/${id}/comments`, { body });
}

export function setStatus(id: number, status: DevRequestStatus, note = ''): Promise<{ id: number; status: DevRequestStatus; changed: boolean }> {
  return api.patch<{ id: number; status: DevRequestStatus; changed: boolean }>(`/dev-requests/${id}/status`, { status, note });
}

/** 본문에 박힌 그림의 주소 — 편집기·보기 화면이 `<img src>` 로 쓴다 */
export function imageUrl(id: number, imageId: number): string {
  return `/api/dev-requests/${id}/image/${imageId}`;
}
